import { computed, effect, inject, Injector, runInInjectionContext, signal, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { catchError, combineLatest, delay, distinctUntilChanged, map, Observable, of, startWith, switchMap } from 'rxjs';

import { UserService } from '@celum/authentication';
import { hasValueChanged, isEmpty, LocalizedValue } from '@celum/core';
import { SpecFieldInfo, SpecificationParameter } from '@celum/formats/domain';
import { explicitEffect } from '@celum/shared/util';

import { SpecificationStore } from '../store';
import { collectSpecFieldInfo } from '../util/spec-field-info.util';

type MatchAndValue = { match: string; value: string; field: string };

/**
 * Minimal shape `FormatFormServiceBase` needs from the entity it builds a form for (`Format`/`BaseFormat`).
 */
export interface FormatFormFormatLike {
  key?: string;
  filters?: { fileExtensions?: string[] };
  icon?: { id?: string };
  protected?: boolean;
  autoImportExcluded?: boolean;
  name?: Record<string, string>;
  description?: { locales?: Record<string, string>; templateApplied?: boolean };
  parameters?: Record<string, any> & { options?: { values?: Record<string, any> } };
  specificationReference?: { id?: number };
}

/**
 * Minimal shape required from the entity store backing the form service (`FormatStore`/`BaseFormatStore`).
 */
export interface FormatFormStoreLike<TFormat extends FormatFormFormatLike> {
  selectedFormat: Signal<TFormat | undefined>;
  selectedFormatId: Signal<number>;
}

/**
 * Minimal shape required from the CRUD service used for the async unique-key validator
 * (`FormatsService`/`BaseFormatsService`).
 */
export interface FormatDefinitionsServiceLike {
  getFormatByKey(key: string): Observable<{ totalElements: number }>;
}

/**
 * Common fields shared by `CreateFormat`/`CreateBaseFormat` - everything except `autoImportExcluded`, which only
 * `BaseFormat` has. Returned by {@link FormatFormServiceBase.buildCommonFormatFields}; concrete subclasses merge in
 * whatever extra fields their own `TCreate` type requires (only `autoImportExcluded` for base formats today).
 */
export interface FormatCommonCreateFields {
  key: string;
  name: Record<string, string>;
  description: { locales: Record<string, string>; templateApplied: boolean };
  specificationReference: { key: string };
  parameters: Record<string, any>;
  filters: { fileExtensions: string[] };
  icon: { id: string };
  protected: boolean;
}

type FormatDiffObject = {
  isProtected: boolean;
  autoImportExcluded: boolean;
  fileExtension: string[];
  name: LocalizedValue;
  icon: string;
  description: LocalizedValue;
  parameters: Record<string, any>;
};

export abstract class FormatFormServiceBase<TFormat extends FormatFormFormatLike, TCreate> {
  public formatForm = inject(FormBuilder).group({
    key: new FormControl('', {
      validators: [Validators.required, Validators.maxLength(55), Validators.pattern(/^[a-zA-Z0-9-_]+$/)],
      asyncValidators: [this.uniqueKeyValidator()],
      updateOn: 'change'
    }),
    isProtected: new FormControl(false),
    // Unused for tenant formats (`autoImportExcluded` only applies to `BaseFormat`) - present only so the
    // format-view-dialog template, shared between both dialogs, type-checks for both hosts.
    autoImportExcluded: new FormControl(false),
    icon: new FormControl('default', Validators.required),
    name: new FormGroup<{ [key: string]: FormControl }>({
      en: new FormControl('', [Validators.required, Validators.maxLength(55)])
    }),
    description: new FormGroup<{ [key: string]: FormControl }>({
      en: new FormControl<string>('', [Validators.maxLength(255)])
    }),
    parameters: new FormGroup<Record<string, FormControl>>({}),
    fileExtension: new FormControl<string[]>([], Validators.required)
  });

  public descriptionPlaceholder?: Signal<string>;
  public saveDisabled?: Signal<boolean>;
  public formatParametersChanged?: Signal<boolean>;
  public warningDialogDisabled: boolean;

  protected injector = inject(Injector);
  protected specStore = inject(SpecificationStore);
  protected userService = inject(UserService);

  protected abstract readonly formatStore: FormatFormStoreLike<TFormat>;
  protected abstract readonly formatDefinitionsService: FormatDefinitionsServiceLike;
  protected abstract readonly logPrefix: string;

  private static readonly DONT_SHOW_FORMAT_PARAMETERS_WARNING_KEY = 'dontShowFormatParametersWarning';

  constructor() {
    this.warningDialogDisabled = this.isWarningDialogDisabled();

    explicitEffect(
      this.specStore.specParameters,
      this.specStore.selectedSpec,
      computed(() => this.formatStore.selectedFormat()),
      (specParameters, selectedSpec, selectedFormat) => {
        if (!specParameters || (!selectedSpec && selectedFormat && !selectedFormat?.specificationReference?.id)) {
          this.formatForm.controls.parameters.reset({});
          Object.keys(this.formatForm.controls.parameters.controls).forEach(key => {
            this.formatForm.controls.parameters.removeControl(key);
          });
        } else {
          this.generateSpecControls(specParameters);
        }

        this.generatedPlaceholderForDescription(selectedSpec?.description?.template, specParameters);

        this.initSaveControlSignals();
      }
    );

    effect(() => {
      if (!this.formatStore.selectedFormat()) {
        return;
      }

      if (this.formatStore.selectedFormat().key) {
        this.formatForm.controls.key.disable();
        this.formatForm.controls.key.clearValidators();
        this.formatForm.controls.key.clearAsyncValidators();
      }

      this.formatForm.patchValue({
        key: this.formatStore.selectedFormat().key ?? '',
        fileExtension: this.formatStore.selectedFormat().filters?.fileExtensions ?? [],
        icon: this.formatStore.selectedFormat().icon?.id ?? 'default',
        isProtected: this.formatStore.selectedFormat().protected ?? false,
        autoImportExcluded: this.formatStore.selectedFormat().autoImportExcluded ?? false
      });

      Object.entries(this.formatStore.selectedFormat().name).forEach(([key, value]) => {
        if (key === 'en') {
          this.formatForm.controls.name.get('en').setValue(value);
          return;
        }

        this.formatForm.controls.name.addControl(key, new FormControl(value, Validators.maxLength(55)));
      });

      if (this.formatStore.selectedFormat().description?.locales && !this.formatStore.selectedFormat()?.description?.templateApplied) {
        Object.entries(this.formatStore.selectedFormat().description.locales).forEach(([key, value]) => {
          if (key === 'en') {
            this.formatForm.controls.description.get('en').setValue(value);
            return;
          }

          this.formatForm.controls.description.addControl(key, new FormControl(value, Validators.maxLength(255)));
        });
      }
    });

    explicitEffect(
      this.specStore.fileExtensions,
      computed(() => this.formatStore.selectedFormatId()),
      computed(() => this.formatStore.selectedFormat()),
      this.specStore.selectedSpecId,
      (fileExtensions, selectedFormatId, selectedFormat, selectedSpecId) => {
        const fileExtensionControl = this.formatForm.get('fileExtension');
        if (selectedFormatId && !selectedFormat) {
          return;
        }

        if (selectedFormat?.specificationReference?.id === selectedSpecId) {
          fileExtensionControl.setValue(selectedFormat.filters?.fileExtensions ?? []);
          return;
        }

        fileExtensionControl.setValue(fileExtensions ?? []);
      }
    );
  }

  public abstract generateFormatObject(): TCreate;

  public collectFieldInfo(specParameters: SpecificationParameter): SpecFieldInfo {
    return collectSpecFieldInfo(specParameters);
  }

  public selectAllFileExtensions(): void {
    this.formatForm.controls.fileExtension.setValue(this.specStore.fileExtensions());
  }

  public toggleFileExtension(extension: string, checked: boolean): void {
    const fileExtensionControl = this.formatForm.controls.fileExtension;

    if (checked) {
      fileExtensionControl.setValue([...fileExtensionControl.value, extension]);
      return;
    }

    fileExtensionControl.setValue([...fileExtensionControl.value.filter(ext => ext !== extension)]);
  }

  public removeFileExtension(extension: string): void {
    const currentExtensions = this.formatForm.get('fileExtension').value;
    this.formatForm.controls.fileExtension.setValue(currentExtensions.filter(ext => ext !== extension));
  }

  public clearFileExtensions(): void {
    this.formatForm.controls.fileExtension.setValue([]);
  }

  public disableDialogWarning(): void {
    this.warningDialogDisabled = true;
    try {
      localStorage.setItem(`${this.userService.getCurrentUser().oid}-${FormatFormServiceBase.DONT_SHOW_FORMAT_PARAMETERS_WARNING_KEY}`, 'true');
    } catch {
      console.debug(`${this.logPrefix}: Error while setting local storage item.`);
    }
  }

  /**
   * Validates and assembles the payload fields shared by `CreateFormat`/`CreateBaseFormat`. Returns `null` (and
   * marks the form as touched to surface validation errors) when the form is invalid - subclasses should propagate
   * that `null` from their `generateFormatObject()` override.
   */
  protected buildCommonFormatFields(): FormatCommonCreateFields | null {
    if (this.formatForm.invalid) {
      // In case of invalid form, mark all fields as touched to show validation errors
      this.formatForm.markAllAsTouched();
      this.formatForm.controls.name.markAllAsTouched();
      this.formatForm.controls.description.markAllAsTouched();

      this.formatForm.controls.fileExtension.markAllAsTouched();
      this.formatForm.controls.fileExtension.updateValueAndValidity();

      return null;
    }

    const { key, name, description, icon, isProtected } = this.formatForm.getRawValue();
    const parameters = this.formatForm.controls.parameters.getRawValue();
    const specParameters = this.specStore.specParameters() ?? [];

    const trimmedName = Object.fromEntries(Object.entries(name).map(([locale, value]) => [locale, value?.trim()]));
    const trimmedDescription = Object.fromEntries(Object.entries(description).map(([locale, value]) => [locale, value?.trim()]));

    return {
      key,
      name: trimmedName,
      description: {
        locales: trimmedDescription.en ? trimmedDescription : { en: this.descriptionPlaceholder?.() },
        templateApplied: !!this.descriptionPlaceholder?.() && !trimmedDescription.en
      },
      specificationReference: {
        key: this.specStore.selectedSpec().key
      },
      parameters: buildParametersPayload(specParameters, parameters),
      filters: {
        fileExtensions: this.formatForm.get('fileExtension').value.filter(Boolean)
      },
      icon: { id: icon },
      protected: isProtected
    };
  }

  private isWarningDialogDisabled(): boolean {
    try {
      return localStorage.getItem(`${this.userService.getCurrentUser().oid}-${FormatFormServiceBase.DONT_SHOW_FORMAT_PARAMETERS_WARNING_KEY}`) === 'true';
    } catch {
      console.debug(`${this.logPrefix}: Error while getting local storage item.`);
      return false;
    }
  }

  private generatedPlaceholderForDescription(template: string, specParameters: SpecificationParameter[]): void {
    if (!template) {
      this.descriptionPlaceholder = null;

      // Description should be always provided, if template is not defined then we need to require description
      this.formatForm.controls.description.controls.en.addValidators(Validators.required);
      this.formatForm.controls.description.controls.en.updateValueAndValidity();

      return;
    }

    // If template is defined, we can remove required validator for description as template is the fallback
    this.formatForm.controls.description.controls.en.removeValidators(Validators.required);
    this.formatForm.controls.description.controls.en.updateValueAndValidity();

    if (isEmpty(specParameters)) {
      // we try next time when spec parameters are loaded
      return;
    }

    const fields = template.matchAll(/#{(.*?)}/g);

    const obs$: Observable<MatchAndValue>[] = fields.reduce<Observable<MatchAndValue>[]>((acc, [match, field]) => {
      const fieldPath = field.split('.').filter(path => path !== '$');

      const fieldKey = fieldPath[fieldPath.length - 1];
      if (fieldPath[0] === 'parameters') {
        const parameter = specParameters.find(param => param.key === fieldKey);
        const startValue = this.formatForm.controls.parameters.get(fieldKey).value || parameter?.default || '';
        acc.push(
          this.formatForm.controls.parameters.get(fieldKey).valueChanges.pipe(
            startWith(startValue),
            map(value => ({
              match,
              value,
              field: fieldKey
            }))
          )
        );
      }

      return acc;
    }, []);

    runInInjectionContext(this.injector, () => {
      this.descriptionPlaceholder = toSignal(
        combineLatest(obs$).pipe(
          map(fieldsChange => {
            let placeholder = template;
            fieldsChange.forEach(({ match, value }) => {
              placeholder = placeholder.replace(match, value);
            });

            return placeholder;
          })
        )
      );
    });
  }

  private flattenParameters(parameters: Record<string, any>, prefix = ''): Record<string, any> {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(parameters)) {
      if (key === 'options') {
        continue;
      }

      const fullKey = prefix ? `${prefix}_${key}` : key;
      if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
        Object.assign(result, this.flattenParameters(value, fullKey));
      } else {
        result[fullKey] = value;
      }
    }
    return result;
  }

  private initSaveControlSignals(): void {
    const format = this.formatStore.selectedFormat();
    const parameters = {
      ...this.flattenParameters(format?.parameters ?? {}),
      ...(format?.parameters?.options?.values ?? {})
    };

    const formatObject: FormatDiffObject = {
      name: format?.name,
      description: !format?.description?.templateApplied ? format?.description?.locales : { en: '' },
      parameters,
      fileExtension: format?.filters?.fileExtensions ?? [],
      icon: format?.icon?.id || 'default',
      isProtected: format?.protected || false,
      autoImportExcluded: format?.autoImportExcluded || false
    };

    const excludeNonValues = (obj: Record<string, any>) => Object.fromEntries(Object.entries(obj).filter(([_, value]) => Boolean(value)));

    runInInjectionContext(this.injector, () => {
      this.saveDisabled = toSignal(this.getFormatFormSaveDisabledObservable(excludeNonValues, formatObject));

      if (!format) {
        this.formatParametersChanged = signal(false);
        return;
      }

      this.formatParametersChanged = toSignal(this.getFormatParamChangedObservable(excludeNonValues, formatObject));
    });
  }

  private getFormatParamChangedObservable(
    excludeNonValues: (obj: Record<string, any>) => { [p: string]: any },
    formatObject: FormatDiffObject
  ): Observable<boolean> {
    return combineLatest([
      this.formatForm.controls.parameters.valueChanges.pipe(startWith(this.formatForm.controls.parameters.value), distinctUntilChanged()),
      this.formatForm.controls.fileExtension.valueChanges.pipe(startWith(this.formatForm.controls.fileExtension.value), distinctUntilChanged())
    ]).pipe(
      map(([parameters, fileExtension]) => {
        const changes = { parameters: excludeNonValues(parameters), fileExtension };
        return hasValueChanged({ parameters: excludeNonValues(formatObject.parameters), fileExtension: formatObject.fileExtension }, changes);
      })
    );
  }

  private getFormatFormSaveDisabledObservable(
    excludeNonValues: (obj: Record<string, any>) => { [p: string]: any },
    formatObject: FormatDiffObject
  ): Observable<boolean> {
    return this.formatForm.valueChanges.pipe(
      startWith(this.formatForm.value),
      distinctUntilChanged(),
      map(value => {
        value.parameters = excludeNonValues(value.parameters);
        formatObject.parameters = excludeNonValues(formatObject.parameters);

        return !hasValueChanged(formatObject, value);
      })
    );
  }

  private generateSpecControls(specParameters: SpecificationParameter[]): void {
    const selectedFormat = this.formatStore.selectedFormat();

    specParameters.forEach(param => {
      const savedValue = resolveSavedValue(param, selectedFormat?.parameters);
      const fieldInfo = this.collectFieldInfo(param);
      const defaultValue = resolveDefaultValue(param, fieldInfo);

      this.formatForm.controls.parameters.addControl(param.key, new FormControl(savedValue ?? defaultValue, this.generateValidation(param)));
    });
  }

  private generateValidation(specProp: SpecificationParameter): any {
    const metadata = specProp?.['x-celum-ui-parameters-metadata'];
    if (!metadata || !metadata?.pattern) {
      return null;
    }

    return [Validators.pattern(metadata.pattern)];
  }

  private uniqueKeyValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      return of(control.value).pipe(
        delay(600),
        switchMap(value => this.formatDefinitionsService.getFormatByKey(value)),
        map(existingFormat => (existingFormat.totalElements > 0 ? { keyExists: true } : null)),
        catchError(() => of(null))
      );
    };
  }
}

export function buildParametersPayload(specParameters: SpecificationParameter[], formValues: Record<string, any>): Record<string, any> {
  const { optionsValues, directValues } = partitionParameterValues(specParameters, formValues);

  const hasOptions = Object.keys(optionsValues).length > 0;
  const hasDirect = Object.keys(directValues).length > 0;

  if (hasOptions) {
    return { options: { values: optionsValues, type_: 'spro_options' }, ...directValues };
  }
  if (hasDirect) {
    return { ...directValues };
  }

  // Fallback: spec params not available yet — send raw form values under options
  return { options: { values: { ...formValues }, type_: 'spro_options' } };
}

export function partitionParameterValues(
  specParameters: SpecificationParameter[],
  formValues: Record<string, any>
): { optionsValues: Record<string, any>; directValues: Record<string, any> } {
  const optionsValues: Record<string, any> = {};
  const directValues: Record<string, any> = {};

  for (const param of specParameters) {
    const value = formValues[param.key];
    if (value === null || value === undefined || value === '') {
      continue;
    }

    if (param.parameterSource === 'direct') {
      assignNestedValue(directValues, param.key, value);
    } else {
      optionsValues[param.key] = value;
    }
  }

  return { optionsValues, directValues };
}

export function assignNestedValue(target: Record<string, any>, key: string, value: any): void {
  const parts = key.split('_');
  if (parts.length > 1) {
    const [parent, ...rest] = parts;
    target[parent] = target[parent] ?? {};
    target[parent][rest.join('_')] = value;
  } else {
    target[key] = value;
  }
}

export function resolveSavedValue(param: SpecificationParameter, savedParameters?: Record<string, any> & { options?: { values?: Record<string, any> } }): any {
  if (!savedParameters) {
    return undefined;
  }

  if (param.parameterSource === 'direct') {
    const parts = param.key.split('_');
    if (parts.length > 1) {
      const [parent, ...rest] = parts;
      return savedParameters[parent]?.[rest.join('_')];
    }
    return savedParameters[param.key];
  }

  return savedParameters?.options?.values?.[param.key];
}

export function resolveDefaultValue(param: SpecificationParameter, fieldInfo: SpecFieldInfo): any {
  const isMultiSelect = fieldInfo.dropdown?.selectionType === 'multiple';
  if (isMultiSelect) {
    return fieldInfo.dropdown.defaultValues ?? [];
  }

  return param?.default;
}
